import { createContext, Dispatch, useCallback, useContext, useMemo } from 'react';
import currency from 'currency.js';
import { filter } from 'lodash';

import { HeaderStep } from '@/components/generic/Stepper/types';

import { getInitialState, getOrderItemPrice } from './constants';
import { OrderItemWrapper, State, StepsName } from './types';

type ActionInitial = {
  type: 'INITIAL';
};

type ActionUpdate = {
  type: 'UPDATE';
  payload: Partial<State>;
};

type ActionUpdateOrder = {
  type: 'UPDATE_ORDER';
  payload: Partial<State['order']>;
};

type ActionUpdateVisibleOrderItems = {
  type: 'UPDATE_VISIBLE_ORDER_ITEMS';
  payload: State['visibleOrderItems'];
};

type ActionUpdateProjectOptions = {
  type: 'UPDATE_PROJECT_OPTIONS';
  payload: Partial<State['order']['projectOptions']>;
};

export type Action =
  | ActionInitial
  | ActionUpdate
  | ActionUpdateOrder
  | ActionUpdateProjectOptions
  | ActionUpdateVisibleOrderItems;

export const OrderModalContext = createContext<State>(getInitialState());
export const OrderModalDispatchContext = createContext<Dispatch<Action>>(() => null);

export function reducer(state, action: Action) {
  switch (action.type) {
    case 'UPDATE':
      return {
        ...state,
        ...action.payload,
      };

    case 'UPDATE_ORDER': {
      return {
        ...state,
        order: {
          ...state.order,
          ...action.payload,
        },
      };
    }

    case 'UPDATE_VISIBLE_ORDER_ITEMS': {
      return {
        ...state,
        visibleOrderItems: action.payload,
      };
    }

    case 'UPDATE_PROJECT_OPTIONS': {
      return {
        ...state,
        order: {
          ...state.order,
          projectOptions: {
            ...state.order.projectOptions,
            ...action.payload,
          },
        },
      };
    }

    case 'INITIAL':
      return getInitialState();

    default:
      return state;
  }
}

export function useOrderContext(): State {
  return useContext<State>(OrderModalContext);
}

export function useOrderDispatch() {
  return useContext(OrderModalDispatchContext);
}

export function useOrderUpdate() {
  const dispatch = useOrderDispatch();

  return useCallback(
    (order: State) => {
      dispatch({ type: 'UPDATE', payload: order });
    },
    [dispatch],
  );
}

export function useOrderItemsTotalCost(
  state: State,
  predicate: (item: OrderItemWrapper) => boolean,
) {
  const items = useMemo(() => filter(state.visibleOrderItems, predicate), [state, predicate]);
  const total = useMemo(
    () => items.map(item => getOrderItemPrice(state, item)).reduce((c, n) => c.add(n), currency(0)),
    [items, state],
  );

  return { items, total };
}

export function getStepByName(state: State, name: StepsName): number {
  return state.wizardHeaderSteps.find(step => step.label === name)?.step ?? 1;
}

export function markStepAsCompleted(steps: HeaderStep[], labelToFind: StepsName) {
  return steps.map((step: HeaderStep) => {
    if (step.label === labelToFind) {
      step.isStepComplete = true;
      step.isActive = false;
    }

    return step;
  });
}

// Function to check if the previous steps before the confirmation are completed or not,
// so if we go to any previous step, we can navigate back to the confirmation step
export function getConfirmationIndex(steps: HeaderStep[]) {
  return steps.findIndex((step: HeaderStep) =>
    [StepsName['Request Contract'], StepsName['Confirmation']].includes(step.label),
  );
}
